'use client'

import { cn } from '@/lib/utils'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { cva } from 'class-variance-authority'
import * as React from 'react'

const tooltipVariants = cva(
  'tooltip-content group z-50 overflow-hidden rounded-md bg-popover px-3 py-1.5 text-xs text-neutral-900 shadow-[0px_1px_4px_0px_hsla(0,0%,0%,0.08),0px_0px_24px_0px_hsla(0,0%,0%,0.06)] animate-in fade-in-0',
  {
    variants: {
      variant: {
        default: 'bg-popover',
        dark: 'bg-neutral-100 text-neutral-900',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

interface TooltipProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>,
    'ref' | 'content'
  > {
  content: React.ReactNode
  variant?: 'default' | 'dark'
  open?: boolean
  delayDuration?: number
  onOpenChange?: (open: boolean) => void
  trigger?: 'hover' | 'click'
}

const Tooltip = ({
  content,
  variant,
  children,
  side = 'top',
  sideOffset = 4,
  open: controlledOpen,
  onOpenChange,
  delayDuration = 200,
  trigger = 'hover',
  ...props
}: TooltipProps) => {
  const [open, setOpen] = React.useState(false)
  const isControlled = controlledOpen !== undefined

  const handleOpenChange = (open: boolean) => {
    if (!isControlled) {
      setOpen(open)
    }
    onOpenChange?.(open)
  }

  const handleClick = () => {
    if (trigger === 'click') {
      handleOpenChange(!open)
    }
  }

  return (
    <TooltipPrimitive.Root
      open={isControlled ? controlledOpen : open}
      onOpenChange={handleOpenChange}
      delayDuration={trigger === 'click' ? 0 : delayDuration}
    >
      <TooltipPrimitive.Trigger
        asChild
        onClick={trigger === 'click' ? handleClick : undefined}
      >
        {children}
      </TooltipPrimitive.Trigger>

      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          sideOffset={sideOffset}
          className={cn(tooltipVariants({ variant }))}
          {...props}
        >
          {content}
          <TooltipPrimitive.Arrow
            className={variant === 'dark' ? 'fill-neutral-100' : 'fill-popover'}
          />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
}

const TooltipProvider = TooltipPrimitive.Provider

export { Tooltip, TooltipProvider }
